import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchPopularSearch, fetchSuggestions } from '../Search/Search.action';
import { getDictionaryValue } from '../../Services/Language.service';

const Search = () => {
    const [isMenuOpened, setIsMenuOpened] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [suggestions, setSuggestions] = useState(null);
    const dispatch = useDispatch();
    const currentLanguageIso = useSelector((state) => state.language.currentLanguageIso);
    const dictionaries = useSelector((state) => state.language.dictionaries);
    const searchData = useSelector((state) => state.search.searchData);
    const isSearchEnabled = Boolean(searchData?.searchResultPageUrl);
    const searchQueryMinLength = 3;
    const debounceDelay = 1000;

    useEffect(() => {
        const fetchData = async () => {
            try {
                dispatch(fetchPopularSearch());
            } catch (error) {
                console.error('Error fetching languages:', error);
            }
        };
        fetchData();
    }, [dispatch]);

    const fetchSuggestionsDebounced = useCallback(
        (iso, query) => {
            if (query.length >= searchQueryMinLength) {
                dispatch(fetchSuggestions(iso, query)).then((data) => {
                    setSuggestions(data);
                }).catch((error) => {
                    console.error('Error fetching suggestions:', error);
                });
            }
        },
        [dispatch]
    );

    useEffect(() => {
        if (!isSearchEnabled) {
            return;
        }

        const handler = setTimeout(() => {
            fetchSuggestionsDebounced(currentLanguageIso, searchInput);
        }, debounceDelay);

        return () => {
            clearTimeout(handler);
        };
    }, [searchInput, fetchSuggestionsDebounced, currentLanguageIso, isSearchEnabled]);

    const handleClick = () => {
        setIsMenuOpened(!isMenuOpened);
    }

    const handleInputChange = async (event) => {
        setSearchInput(event.target.value);
        if (event.target.value.length < searchQueryMinLength) {
            setSuggestions(null);
        }
    }

    const handleSubmit = async (e = null) => {
        if (e) {
            e.preventDefault();
        }
        const searchResultPageUrl = searchData?.searchResultPageUrl;
        if (searchResultPageUrl) {
            window.location.href = `${searchResultPageUrl}?q=${encodeURIComponent(searchInput)}`;
        }
    }

    const handleSubmitBySuggestion = async (suggestion) => {
        const searchResultPageUrl = searchData?.searchResultPageUrl;
        if (searchResultPageUrl) {
            window.location.href = `${searchResultPageUrl}?q=${encodeURIComponent(suggestion)}`;
        }
    }

    const handleSuggestionClick = (suggestion) => {
        handleSubmitBySuggestion(suggestion);
    };

    return (
        <Fragment>
            {dictionaries && isSearchEnabled && (
                <div className='search-container'>
                    <div className="search__menu" onClick={handleClick}>
                        <p>{getDictionaryValue("General Fields.Search", dictionaries)}</p>
                        <span></span>
                    </div>
                    {isMenuOpened && (
                        <div id='search__content-container' className='search__content-container'>
                            <p className='search__content-container__close' onClick={handleClick}>{getDictionaryValue("General Fields.Close", dictionaries)}</p>
                            <form className="search__content-container__search" onSubmit={handleSubmit}>
                                <input
                                    placeholder={getDictionaryValue("Search.Search Placeholder", dictionaries)}
                                    value={searchInput}
                                    onChange={handleInputChange}
                                />
                                <span></span>
                            </form>
                            <div className="search__content-container__result">
                                <p className="search-titles">{searchInput === '' ? getDictionaryValue("Search.Popular Searches", dictionaries) : getDictionaryValue("Search.Search Result", dictionaries)}</p>
                                {searchInput === '' && searchData.popularSearches && searchData.popularSearches.length > 0 && (
                                    <div className="result-container">
                                        {searchData.popularSearches.map((link, index) => (
                                            <div key={index} className="popular-search">
                                                <a href={link.url}>
                                                    <p>{link.title}</p>
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {searchInput !== '' && searchInput.length >= searchQueryMinLength && suggestions && suggestions.length > 0 && (
                                    <div className="suggestion-container">
                                        {suggestions.map((suggestion, index) => (
                                            <div className='search-suggestion' key={index} onClick={() => handleSuggestionClick(suggestion)}>{suggestion}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Fragment>
    );
};

export default Search;
